/* eslint-disable prettier/prettier -- behoud de afgesproken witregels binnen conditionals. */
/* eslint-disable require-jsdoc */
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { EXRLoader } from 'three-stdlib'
import { Environment } from '@react-three/drei'
import { BackSide, DataTexture, LinearFilter } from 'three'
import { fetchAssetBuffer, isAbortError, throwIfAborted } from './assetRequests'

const envLoader = new EXRLoader()

export async function loadEXRTexture(url, signal) {
  const buffer = await fetchAssetBuffer(url, signal)

  throwIfAborted(signal)

  const data = envLoader.parse(buffer)

  throwIfAborted(signal)

  // neem dezelfde defaults over als DataTextureLoader en EXRLoader.load.
  const texture = new DataTexture(
    data.data,
    data.width,
    data.height,
    data.format,
    data.type,
  )
  texture.colorSpace = data.colorSpace
  texture.minFilter = LinearFilter
  texture.magFilter = LinearFilter
  texture.generateMipmaps = false
  texture.flipY = false
  texture.needsUpdate = true

  return texture
}

const constructEnvironmentMapURL = (map) =>
  `https://3dassets.royalbotania.com/envmaps/${map}.exr`

export function Env({ background, map, rotation, onDone, onError, requestScope }) {
  const blur = 0

  const intensity = 0.5

  const [envMap, setEnvMap] = useState(null)

  const callbacksRef = useRef({ onDone, onError })
  callbacksRef.current = { onDone, onError }

  // als de 'map' aangepast wordt, set URL
  const textureUrl = useMemo(() => constructEnvironmentMapURL(map), [map])

  useEffect(() => {
    const controller = requestScope?.createController() || new AbortController()

    let loadedTexture = null

    setEnvMap(null)
    callbacksRef.current.onDone?.(false)

    loadEXRTexture(textureUrl, controller.signal)
      .then((texture) => {
        if (controller.signal.aborted) {

          texture.dispose()
          return

        }

        loadedTexture = texture

        setEnvMap(texture)
        callbacksRef.current.onDone?.(true)
      })
      .catch((error) => {
        if (!controller.signal.aborted && !isAbortError(error)) {

          callbacksRef.current.onError?.(error)

        }
      })
      .finally(() => requestScope?.release(controller))

    return () => {
      controller.abort()

      requestScope?.release(controller)

      // ruim de texture van dit effect op, niet een verouderde statewaarde.
      loadedTexture?.dispose()
    }
  }, [textureUrl, requestScope])

  if (!envMap) {

    return null

  }

  return (
    <Environment background={background} blur={blur}>
      {background && <color attach="background" args={['black']} />}
      <mesh rotation={[0, rotation, 0]} scale={10}>
        <sphereGeometry />
        <meshBasicMaterial
          transparent
          opacity={intensity}
          map={envMap}
          side={BackSide}
          toneMapped={false}
        />
      </mesh>
    </Environment>
  )
}
